import React, { useRef, useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const GameContainer = styled(motion.div)`
  width: 100%;
  max-width: 880px;
  height: 580px;
  background: rgba(13, 15, 23, 0.98);
  border: 2px solid #38bdf8;
  border-radius: 20px;
  box-shadow: 0 0 40px rgba(56, 189, 248, 0.3), inset 0 0 30px rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  font-family: "Karla", sans-serif;
  color: #ffffff;
  margin: 0 auto;

  &:fullscreen {
    max-width: 100vw;
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    border: none;
  }
  &:-webkit-full-screen {
    max-width: 100vw;
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    border: none;
  }

  @media (max-width: 768px) {
    height: 540px;
    border-radius: 14px;
  }
  @media (max-width: 480px) {
    height: 520px;
    border-radius: 12px;
  }
`;

const CanvasHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.8rem 1.25rem;
  background: rgba(8, 10, 18, 0.95);
  border-bottom: 1px solid rgba(56, 189, 248, 0.35);
  z-index: 5;
  flex-shrink: 0;

  @media (max-width: 520px) {
    padding: 0.6rem 0.85rem;
    gap: 0.5rem;
    flex-direction: column;
    align-items: stretch;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 1.2rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 520px) {
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
  }
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 800;
  font-size: 0.95rem;

  span {
    color: ${(props) => props.color || "#38bdf8"};
  }

  @media (max-width: 480px) {
    font-size: 0.82rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 520px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid ${(props) => props.borderColor || "rgba(255, 255, 255, 0.25)"};
  color: ${(props) => props.color || "#e2e8f0"};
  padding: 0.38rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 700;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.3rem 0.6rem;
  }
`;

// Wrapper area that contains the canvas and overlay so they never overlap the header
const CanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const GameCanvas = styled.canvas`
  width: 100%;
  flex-grow: 1;
  cursor: ${(props) => (props.isPlaying ? "none" : "default")};
  display: block;
  touch-action: none;
`;

const MobileControls = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    padding: 0.6rem 1rem;
    background: rgba(10, 12, 20, 0.96);
    border-top: 1px solid rgba(56, 189, 248, 0.25);
    z-index: 5;
    gap: 1rem;
    flex-shrink: 0;
  }
`;

const TouchPadButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.25), rgba(99, 102, 241, 0.25));
  border: 1.5px solid #38bdf8;
  color: #ffffff;
  padding: 0.65rem;
  border-radius: 12px;
  font-weight: 800;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  user-select: none;
  touch-action: manipulation;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &:active {
    background: linear-gradient(135deg, #0ea5e9, #6366f1);
    transform: scale(0.96);
  }
`;

const Overlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(8, 10, 18, 0.92);
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  text-align: center;
  z-index: 10;
  box-sizing: border-box;
  overflow-y: auto;
`;

const GameTitle = styled.h2`
  font-size: clamp(1.4rem, 4.5vw, 2.6rem);
  font-weight: 800;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #38bdf8, #a855f7, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const SubText = styled.p`
  color: #94a3b8;
  max-width: 500px;
  line-height: 1.55;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;

  @media (max-width: 480px) {
    font-size: 0.82rem;
    margin-bottom: 1.1rem;
  }
`;

const PlayButton = styled(motion.button)`
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  color: white;
  border: none;
  padding: 0.8rem 2.2rem;
  font-size: 1.05rem;
  font-weight: 700;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(14, 165, 233, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 480px) {
    padding: 0.65rem 1.5rem;
    font-size: 0.95rem;
  }

  &:hover {
    background: linear-gradient(135deg, #38bdf8, #818cf8);
  }
`;

// Synthesizer
class SoundController {
  constructor() {
    this.ctx = null;
    this.enabled = true;
  }
  init() {
    if (!this.ctx) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) this.ctx = new AudioContext();
    }
  }
  playCoin() {
    if (!this.enabled || !this.ctx) return;
    try {
      if (this.ctx.state === "suspended") this.ctx.resume();
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(587.33, this.ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(880, this.ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.18, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.15);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.15);
    } catch (e) {}
  }
  playPowerup() {
    if (!this.enabled || !this.ctx) return;
    try {
      if (this.ctx.state === "suspended") this.ctx.resume();
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(440, this.ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, this.ctx.currentTime + 0.25);
      gain.gain.setValueAtTime(0.2, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.25);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.25);
    } catch (e) {}
  }
  playHit() {
    if (!this.enabled || !this.ctx) return;
    try {
      if (this.ctx.state === "suspended") this.ctx.resume();
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(160, this.ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(45, this.ctx.currentTime + 0.28);
      gain.gain.setValueAtTime(0.25, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, this.ctx.currentTime + 0.28);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.28);
    } catch (e) {}
  }
}

const soundCtrl = new SoundController();

const SKILLS_LIST = [
  { name: "React", symbol: "⚛️", color: "#61DAFB", xp: 150 },
  { name: "Node.js", symbol: "🟢", color: "#68A063", xp: 150 },
  { name: "Solana", symbol: "◎", color: "#14F195", xp: 220 },
  { name: "Next.js", symbol: "N", color: "#FFFFFF", xp: 160 },
  { name: "TypeScript", symbol: "TS", color: "#3178C6", xp: 130 },
  { name: "MongoDB", symbol: "🍃", color: "#4DB33D", xp: 130 },
  { name: "Tailwind", symbol: "🌊", color: "#38B2AC", xp: 110 },
  { name: "Python", symbol: "🐍", color: "#FFD43B", xp: 140 },
];

const POWERUPS_LIST = [
  { name: "Coffee Boost", symbol: "☕", color: "#F59E0B", xp: 500, isPowerup: true },
];

const BUGS_LIST = [
  { name: "TypeError", symbol: "👾", color: "#EF4444" },
  { name: "NullPointer", symbol: "☣️", color: "#F97316" },
  { name: "MemoryLeak", symbol: "💣", color: "#EC4899" },
  { name: "404 Bug", symbol: "🕷️", color: "#EF4444" },
];

const SkillGameArcade = ({ onExit }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState("START");
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(1);
  const [lives, setLives] = useState(3);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem("dev_arcade_hi") || "0", 10);
  });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const touchKeys = useRef({ Left: false, Right: false });

  const toggleSound = () => {
    soundCtrl.enabled = !soundEnabled;
    setSoundEnabled(!soundEnabled);
  };

  const toggleFullscreen = () => {
    const elem = containerRef.current;
    if (!elem) return;
    if (!document.fullscreenElement) {
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      setIsFullscreen(false);
    }
  };

  const startGame = useCallback(() => {
    soundCtrl.init();
    setScore(0);
    setCombo(1);
    setLives(3);
    setGameState("PLAYING");
  }, []);

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  useEffect(() => {
    if (gameState !== "PLAYING") return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let animationId;
    let width = canvas.clientWidth;
    let height = canvas.clientHeight;
    canvas.width = width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const isSmall = width < 500;
    const paddle = {
      width: isSmall ? 100 : 130,
      height: 24,
      x: width / 2 - (isSmall ? 50 : 65),
      y: height - 42,
      speed: isSmall ? 7.5 : 9.5,
    };

    let items = [];
    let particles = [];
    let floatingTexts = [];
    let spawnCounter = 0;
    let currentLives = 3;
    let currentScore = 0;
    let currentCombo = 1;
    let screenShake = 0;

    const keys = { Left: false, Right: false };

    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keys.Left = true;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keys.Right = true;
    };
    const handleKeyUp = (e) => {
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") keys.Left = false;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") keys.Right = false;
    };

    const handlePointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const x = clientX - rect.left;
      paddle.x = Math.max(0, Math.min(width - paddle.width, x - paddle.width / 2));
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    canvas.addEventListener("mousemove", handlePointerMove);
    canvas.addEventListener("touchmove", handlePointerMove);

    const drawHexagonChip = (x, y, radius, color, symbol, name, isBug, isPowerup) => {
      ctx.save();
      ctx.translate(x, y);

      ctx.shadowColor = color;
      ctx.shadowBlur = 15;

      if (isBug) {
        ctx.beginPath();
        for (let j = 0; j < 10; j++) {
          const angle = (j / 10) * Math.PI * 2;
          const r = j % 2 === 0 ? radius * 1.3 : radius * 0.75;
          const px = Math.cos(angle) * r;
          const py = Math.sin(angle) * r;
          if (j === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        const bugGrad = ctx.createRadialGradient(0, 0, 2, 0, 0, radius * 1.3);
        bugGrad.addColorStop(0, "#fee2e2");
        bugGrad.addColorStop(0.5, "#ef4444");
        bugGrad.addColorStop(1, "#7f1d1d");
        ctx.fillStyle = bugGrad;
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 13px Karla, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(symbol || "👾", 0, -2);
      } else {
        ctx.beginPath();
        for (let j = 0; j < 6; j++) {
          const angle = (j / 6) * Math.PI * 2 + Math.PI / 6;
          const px = Math.cos(angle) * radius;
          const py = Math.sin(angle) * radius;
          if (j === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();

        const chipGrad = ctx.createLinearGradient(-radius, -radius, radius, radius);
        chipGrad.addColorStop(0, "rgba(30, 41, 59, 0.95)");
        chipGrad.addColorStop(0.5, color);
        chipGrad.addColorStop(1, "rgba(15, 23, 42, 0.95)");
        ctx.fillStyle = chipGrad;
        ctx.fill();

        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.65, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
        ctx.fill();

        ctx.shadowBlur = 0;
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 12px Karla, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(symbol || name.slice(0, 2), 0, -2);
      }

      ctx.shadowBlur = 0;
      ctx.fillStyle = isBug ? "#ef4444" : "#ffffff";
      ctx.font = "bold 10px Karla, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(name, 0, radius + 13);

      ctx.restore();
    };

    const drawSpaceship = (px, py, w, h) => {
      ctx.save();
      ctx.translate(px + w / 2, py);

      ctx.beginPath();
      ctx.moveTo(0, -8);
      ctx.lineTo(w / 2, h);
      ctx.lineTo(w / 4, h - 5);
      ctx.lineTo(-w / 4, h - 5);
      ctx.lineTo(-w / 2, h);
      ctx.closePath();

      const hullGrad = ctx.createLinearGradient(0, -8, 0, h);
      hullGrad.addColorStop(0, "#38bdf8");
      hullGrad.addColorStop(0.5, "#3b82f6");
      hullGrad.addColorStop(1, "#1e1b4b");
      ctx.fillStyle = hullGrad;
      ctx.shadowColor = "#38bdf8";
      ctx.shadowBlur = 12;
      ctx.fill();

      ctx.strokeStyle = "#93c5fd";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.beginPath();
      ctx.ellipse(0, h * 0.35, 8, 11, 0, 0, Math.PI * 2);
      const canopyGrad = ctx.createRadialGradient(0, h * 0.3, 1, 0, h * 0.35, 8);
      canopyGrad.addColorStop(0, "#ffffff");
      canopyGrad.addColorStop(0.4, "#38bdf8");
      canopyGrad.addColorStop(1, "#0284c7");
      ctx.fillStyle = canopyGrad;
      ctx.fill();

      ctx.fillStyle = "#64748b";
      ctx.fillRect(-w / 2 - 2, h - 10, 4, 10);
      ctx.fillRect(w / 2 - 2, h - 10, 4, 10);

      ctx.shadowBlur = 15;
      ctx.shadowColor = "#00f0ff";
      ctx.beginPath();
      ctx.moveTo(-w / 4, h - 5);
      ctx.lineTo(0, h + 14 + Math.random() * 8);
      ctx.lineTo(w / 4, h - 5);
      ctx.closePath();
      const flameGrad = ctx.createLinearGradient(0, h - 5, 0, h + 20);
      flameGrad.addColorStop(0, "#ffffff");
      flameGrad.addColorStop(0.4, "#38bdf8");
      flameGrad.addColorStop(1, "transparent");
      ctx.fillStyle = flameGrad;
      ctx.fill();

      ctx.restore();
    };

    const spawnItem = () => {
      const rand = Math.random();
      let data, isBug = false, isPowerup = false;

      if (rand < 0.27) {
        isBug = true;
        data = BUGS_LIST[Math.floor(Math.random() * BUGS_LIST.length)];
      } else if (rand > 0.94) {
        isPowerup = true;
        data = POWERUPS_LIST[0];
      } else {
        data = SKILLS_LIST[Math.floor(Math.random() * SKILLS_LIST.length)];
      }

      items.push({
        x: Math.random() * (width - 80) + 40,
        y: -35,
        radius: isBug ? 18 : isPowerup ? 20 : 18,
        speed: Math.random() * 1.5 + 2 + Math.min(3, currentScore / 1400),
        isBug,
        isPowerup,
        name: data.name,
        symbol: data.symbol,
        color: data.color,
        xp: data.xp || 0,
      });
    };

    const createExplosion = (x, y, color) => {
      for (let i = 0; i < 18; i++) {
        particles.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 8.5,
          vy: (Math.random() - 0.5) * 8.5,
          radius: Math.random() * 4 + 2,
          color,
          alpha: 1,
        });
      }
    };

    const spawnPopupText = (text, x, y, color) => {
      floatingTexts.push({
        text,
        x,
        y,
        vy: -2,
        alpha: 1,
        color,
      });
    };

    const loop = () => {
      if (canvas.clientWidth !== width || canvas.clientHeight !== height) {
        width = canvas.clientWidth;
        height = canvas.clientHeight;
        canvas.width = width * window.devicePixelRatio;
        canvas.height = height * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        paddle.y = height - 42;
      }

      ctx.save();
      if (screenShake > 0) {
        ctx.translate((Math.random() - 0.5) * screenShake, (Math.random() - 0.5) * screenShake);
        screenShake *= 0.88;
        if (screenShake < 0.5) screenShake = 0;
      }

      ctx.clearRect(-10, -10, width + 20, height + 20);

      if (keys.Left || touchKeys.current.Left) paddle.x = Math.max(0, paddle.x - paddle.speed);
      if (keys.Right || touchKeys.current.Right) paddle.x = Math.min(width - paddle.width, paddle.x + paddle.speed);

      ctx.strokeStyle = "rgba(56, 189, 248, 0.07)";
      ctx.lineWidth = 1;
      for (let gx = 0; gx < width; gx += 45) {
        ctx.beginPath();
        ctx.moveTo(gx, 0);
        ctx.lineTo(gx, height);
        ctx.stroke();
      }

      drawSpaceship(paddle.x, paddle.y, paddle.width, paddle.height);

      spawnCounter++;
      if (spawnCounter > Math.max(25, 50 - Math.floor(currentScore / 350))) {
        spawnItem();
        spawnCounter = 0;
      }

      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        item.y += item.speed;

        drawHexagonChip(
          item.x,
          item.y,
          item.radius,
          item.color,
          item.symbol,
          item.name,
          item.isBug,
          item.isPowerup
        );

        if (
          item.y + item.radius >= paddle.y &&
          item.y - item.radius <= paddle.y + paddle.height &&
          item.x >= paddle.x - 14 &&
          item.x <= paddle.x + paddle.width + 14
        ) {
          if (item.isBug) {
            soundCtrl.playHit();
            currentLives -= 1;
            currentCombo = 1;
            screenShake = 16;
            setLives(currentLives);
            setCombo(1);
            createExplosion(item.x, item.y, "#ef4444");
            spawnPopupText("-1 ❤️ LIFE!", item.x, item.y - 10, "#ef4444");
          } else if (item.isPowerup) {
            soundCtrl.playPowerup();
            const points = item.xp * currentCombo;
            currentScore += points;
            currentCombo = Math.min(10, currentCombo + 2);
            setScore(currentScore);
            setCombo(currentCombo);
            createExplosion(item.x, item.y, "#fbbf24");
            spawnPopupText(`+${points} COFFEE!`, item.x, item.y - 10, "#fbbf24");
          } else {
            soundCtrl.playCoin();
            const points = item.xp * currentCombo;
            currentScore += points;
            currentCombo = Math.min(10, currentCombo + 1);
            setScore(currentScore);
            setCombo(currentCombo);
            createExplosion(item.x, item.y, item.color);
            spawnPopupText(`+${points} XP`, item.x, item.y - 10, "#38bdf8");
          }
          items.splice(i, 1);

          if (currentLives <= 0) {
            if (currentScore > highScore) {
              setHighScore(currentScore);
              localStorage.setItem("dev_arcade_hi", currentScore.toString());
            }
            ctx.restore();
            setGameState("GAMEOVER");
            return;
          }
          continue;
        }

        if (item.y - item.radius > height + 20) {
          items.splice(i, 1);
          if (!item.isBug && !item.isPowerup) {
            currentCombo = 1;
            setCombo(1);
          }
        }
      }

      for (let i = floatingTexts.length - 1; i >= 0; i--) {
        const ft = floatingTexts[i];
        ft.y += ft.vy;
        ft.alpha -= 0.025;
        if (ft.alpha <= 0) {
          floatingTexts.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.globalAlpha = ft.alpha;
        ctx.fillStyle = ft.color;
        ctx.font = "bold 14px Karla, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(ft.text, ft.x, ft.y);
        ctx.restore();
      }

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= 0.035;
        if (p.alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      ctx.restore();
      animationId = requestAnimationFrame(loop);
    };

    animationId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      canvas.removeEventListener("mousemove", handlePointerMove);
      canvas.removeEventListener("touchmove", handlePointerMove);
    };
  }, [gameState, highScore]);

  const getRank = (sc) => {
    if (sc > 5000) return "Principal Web3 Architect 👑";
    if (sc > 3000) return "Senior Tech Lead 🚀";
    if (sc > 1500) return "Full Stack Sorcerer ⚡";
    if (sc > 500) return "Code Knight 🛡️";
    return "Junior Dev Intern 🌱";
  };

  return (
    <GameContainer
      ref={containerRef}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <CanvasHeader>
        <StatsRow>
          <StatBadge color="#38bdf8">
            Score: <span>{score}</span>
          </StatBadge>
          <StatBadge color="#a855f7">
            Streak: <span>{combo}x</span>
          </StatBadge>
          <StatBadge color="#f43f5e">
            Lives: <span>{"❤️".repeat(Math.max(0, lives))}</span>
          </StatBadge>
        </StatsRow>
        <ButtonGroup>
          <ActionButton onClick={toggleFullscreen} borderColor="#38bdf8" color="#38bdf8">
            {isFullscreen ? "🔲 Exit Fullscreen" : "⛶ Fullscreen"}
          </ActionButton>
          <ActionButton onClick={toggleSound}>
            {soundEnabled ? "🔊 SFX" : "🔇 MUTE"}
          </ActionButton>
          {onExit && (
            <ActionButton onClick={onExit} borderColor="#f43f5e" color="#f43f5e">
              ✕ Tree Mode
            </ActionButton>
          )}
        </ButtonGroup>
      </CanvasHeader>

      {/* CanvasWrapper ensures the canvas and start/gameover overlays sit strictly below the header */}
      <CanvasWrapper>
        <GameCanvas ref={canvasRef} isPlaying={gameState === "PLAYING"} />

        {gameState === "PLAYING" && (
          <MobileControls>
            <TouchPadButton
              onTouchStart={() => (touchKeys.current.Left = true)}
              onTouchEnd={() => (touchKeys.current.Left = false)}
              onMouseDown={() => (touchKeys.current.Left = true)}
              onMouseUp={() => (touchKeys.current.Left = false)}
            >
              ◀ Slide Left
            </TouchPadButton>
            <TouchPadButton
              onTouchStart={() => (touchKeys.current.Right = true)}
              onTouchEnd={() => (touchKeys.current.Right = false)}
              onMouseDown={() => (touchKeys.current.Right = true)}
              onMouseUp={() => (touchKeys.current.Right = false)}
            >
              Slide Right ▶
            </TouchPadButton>
          </MobileControls>
        )}

        {gameState === "START" && (
          <Overlay initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <GameTitle>Skill Collector Arcade</GameTitle>
            <SubText>
              Pilot your Cyber Fighter to collect falling 2D Hexagonal Tech Chips & coffee power-ups (+XP). Dodge radioactive virus bots! Use Fullscreen mode for maximum immersion.
            </SubText>
            <PlayButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={startGame}>
              🎮 Launch Mission
            </PlayButton>
          </Overlay>
        )}

        {gameState === "GAMEOVER" && (
          <Overlay initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <GameTitle style={{ background: "linear-gradient(135deg, #ef4444, #f97316)" }}>
              Mission Complete
            </GameTitle>
            <div style={{ fontSize: "1.1rem", margin: "0.5rem 0 1rem" }}>
              Final XP: <strong style={{ color: "#38bdf8" }}>{score}</strong> | High Score:{" "}
              <strong style={{ color: "#a855f7" }}>{Math.max(score, highScore)}</strong>
            </div>
            <div
              style={{
                background: "rgba(255,255,255,0.08)",
                padding: "0.5rem 1.2rem",
                borderRadius: "50px",
                marginBottom: "1.3rem",
                border: "1px solid rgba(255,255,255,0.15)",
                fontWeight: 700,
                fontSize: "0.9rem",
              }}
            >
              Rank Achieved: <span style={{ color: "#fbbf24" }}>{getRank(score)}</span>
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "center" }}>
              <PlayButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={startGame}>
                🔄 Play Again
              </PlayButton>
              {onExit && (
                <PlayButton
                  style={{ background: "rgba(255,255,255,0.1)", boxShadow: "none" }}
                  onClick={onExit}
                >
                  📜 Return to Skill Tree
                </PlayButton>
              )}
            </div>
          </Overlay>
        )}
      </CanvasWrapper>
    </GameContainer>
  );
};

export default SkillGameArcade;
